import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Typography } from '@scale/scaleui';
import jsonata from 'jsonata';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useEventBus } from 'react-scale/components/scalers/tasks/ThreadedInteraction/events';
import { useUser } from 'react-scale/components/ui/UserContext';
import JSONataEditor from 'react-scale/corp/components/JSONataEditor';
import { useProjectId } from 'react-scale/corp/views/CorpProjectGroupManager/hooks/useProjectId';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import getStatsCollector from 'react-scale/lib/metrics';

const lintersMetrics = getStatsCollector('Linters');

interface JSONataLinterExpressionsColumnProps {
  ruleName?: string;
  expressions: { expression: string; result: boolean | string }[];
  context: any;
  operator: string;
}

const IconMap: Partial<Record<string, IconProp>> = {
  true: 'circle-check',
  false: 'circle-xmark',
};

const IconBgColorMap: Partial<Record<string, string>> = {
  true: 'text-success-700',
  false: 'text-danger-700',
};

const BgColorMap: Partial<Record<string, string>> = {
  true: 'bg-success-50',
  false: 'bg-danger-50',
};

function JSONataLinterExpressionsColumn({
  ruleName,
  expressions,
  context,
}: JSONataLinterExpressionsColumnProps) {
  const [editorsState, setEditorsState] = useState<string[]>(() =>
    expressions.map(expr => expr.expression),
  );
  const [results, setResults] = useState<any[]>(() => expressions.map(expr => expr.result));
  const [hasEditedJsonata, setHasEditedJsonata] = useState(false);

  const projectId = useProjectId();
  const user = useUser();
  const isOperatorGroupEng = useUnleashFeatureFlag(
    'is-operator-group-eng',
    { userId: user.id },
    false,
  );

  const eventBus = useEventBus();

  // Re-evaluate expressions whenever the context changes
  useEffect(() => {
    const evaluateExpressions = async () => {
      const newResults = await Promise.all(
        editorsState.map(async expression => {
          try {
            const jsonataExpression = jsonata(expression);
            const result = await jsonataExpression.evaluate(context);
            return result === true || result === false ? result : JSON.stringify(result);
          } catch (error) {
            return 'Your JSONata expression has a syntax error!';
          }
        }),
      );
      setResults(newResults);
    };

    evaluateExpressions();
  }, [context, editorsState]); // Re-run when context or expressions change

  useEffect(() => {
    if (hasEditedJsonata) {
      /* eslint-disable scale/no-high-cardinality-metric-tags -- Legacy metric, pending review */
      lintersMetrics.incr('jsonataPlayground.expression.edited', 1, {
        isOperator: isOperatorGroupEng ? 'false' : 'true',
        projectId,
        userId: user.id,
      });
      /* eslint-enable scale/no-high-cardinality-metric-tags */
    }
  }, [hasEditedJsonata]);

  const handleEditorChange = async (index: number, newValue: string | undefined) => {
    if (!hasEditedJsonata) {
      setHasEditedJsonata(true);
    }
    const newEditorsState = [...editorsState];
    newEditorsState[index] = newValue ?? editorsState[index];
    setEditorsState(newEditorsState);

    try {
      const expression = jsonata(newEditorsState[index]);
      const result = await expression.evaluate(context);
      const newResults = [...results];
      newResults[index] = result === true || result === false ? result : JSON.stringify(result);
      setResults(newResults);
    } catch (error) {
      const newResults = [...results];
      newResults[index] = 'Error';
      setResults(newResults);
    }
  };

  const computeFinalResult = useMemo(() => {
    return results.every(result => result === true);
  }, [results]); // Recompute when results or operator change

  const getResultDisplay = (result: any, isFinal: boolean = false) => {
    let displayText: string;
    let icon: IconProp = IconMap[result] || 'exclamation-triangle';
    let iconColorClass = IconBgColorMap[result] || 'text-warning-700';
    let bgColorClass = BgColorMap[result] || 'bg-warning-50';

    if (isFinal) {
      displayText = result === true ? 'Linter will fire' : 'Linter will NOT fire';
    } else if (result === true) {
      displayText = 'true';
    } else if (result === false) {
      displayText = 'false';
    } else {
      displayText = 'Your JSONata expression must evaluate to true or false.';
      icon = 'exclamation-triangle';
      iconColorClass = 'text-warning-700';
      bgColorClass = 'bg-warning-50';
    }

    return (
      <div className={`p-2 rounded ${bgColorClass} flex flex-col`}>
        <div className="flex items-center">
          <FontAwesomeIcon icon={icon} className={`mr-2 ${iconColorClass}`} />
          <span className="text-sm font-bold">{displayText}</span>
        </div>
        {!isFinal && result !== true && result !== false && (
          <div className="mt-2 text-sm">Current value: {String(result)}</div>
        )}
      </div>
    );
  };

  const editorHeight = expressions.length === 1 ? '400px' : '200px';

  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(500px, 1fr)' }}>
      <div className="border border-neutral-300 p-3 rounded-md mb-1">
        <Typography.Subtitle2>Result</Typography.Subtitle2>
        <div className="mt-3">{getResultDisplay(computeFinalResult, true)}</div>
        <div className="mt-3">Linter will fire if expression evaluates to true</div>
      </div>

      {editorsState.map((expr, index) => (
        <div key={index} className="border border-neutral-300 p-3 rounded-md mb-1">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography.Subtitle2>Linter Expression {index + 1} </Typography.Subtitle2>

            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (!ruleName) return;

                eventBus.emit('jsonataplayground:sync-expression', {
                  ruleName,
                  expression: expr,
                  expressionIndex: index,
                  result: results[index],
                });
              }}
              icon={{
                type: 'copy',
              }}
              // `ruleName` should never be null/undefined, but we add this check just to be safe
              disabled={ruleName == null}
            >
              Sync Expression With Linter
            </Button>
          </div>
          <div className="mt-2" style={{ height: editorHeight }}>
            <JSONataEditor
              value={expr}
              onChange={newValue => handleEditorChange(index, newValue)}
            />
          </div>
          <Typography.Subtitle2 className="mt-4">Output</Typography.Subtitle2>
          <div className="mt-2">
            {_.isBoolean(results[index]) ? JSON.stringify(results[index]) : results[index]}
          </div>
        </div>
      ))}
    </div>
  );
}

export default JSONataLinterExpressionsColumn;
